import React, { useState, useEffect } from 'react';
import { Link } from 'react-router-dom';
import { Trophy } from 'lucide-react';
import {
    DropdownMenu,
    DropdownMenuContent,
    DropdownMenuItem,
    DropdownMenuLabel,
    DropdownMenuSeparator,
    DropdownMenuTrigger,
} from '@/components/ui/dropdown-menu';
import { Button } from '@/components/ui/button';
import { ScrollArea } from '@/components/ui/scroll-area';
import { useAuth } from '@/features/auth/AuthProvider';
import { useGamification } from '@/hooks/useGamification';

export const BadgesDropdown: React.FC = () => {
    const { currentUser } = useAuth();
    const { fetchUserBadges, loading: gamificationLoading } = useGamification();
    const [userBadges, setUserBadges] = useState<any[]>([]);
    const [loading, setLoading] = useState(false);

    useEffect(() => {
        const loadBadges = async () => {
            if (currentUser) {
                setLoading(true);
                const badges = await fetchUserBadges(currentUser.uid);
                setUserBadges(badges);
                setLoading(false);
            }
        };
        loadBadges();
    }, [currentUser, fetchUserBadges]);

    if (!currentUser) return null;

    return (
        <DropdownMenu>
            <DropdownMenuTrigger asChild>
                <Button variant="ghost" size="icon" className="rounded-full bg-amber-50 hover:bg-amber-100 text-amber-600" aria-label="Badges">
                    <Trophy className="h-6 w-6" />
                </Button>
            </DropdownMenuTrigger>
            <DropdownMenuContent align="end" className="w-64">
                <DropdownMenuLabel className="flex justify-between items-center">
                    <span>My Badges</span>
                    <Link to={`/users/${currentUser.uid}`} className="text-xs font-normal text-muted-foreground hover:underline">
                        View Profile
                    </Link>
                </DropdownMenuLabel>
                <DropdownMenuSeparator />
                <ScrollArea className="h-[200px]">
                    {loading || gamificationLoading ? (
                        <div className="p-4 text-center text-xs text-muted-foreground">Loading badges...</div>
                    ) : userBadges.length === 0 ? (
                        <div className="flex flex-col items-center justify-center h-[150px] text-muted-foreground text-xs p-4 text-center">
                            <Trophy className="h-10 w-10 mb-2 opacity-30 text-amber-500" />
                            <p>No badges yet.</p>
                            <p className="mt-1">Complete challenges to earn!</p>
                        </div>
                    ) : (
                        <div className="p-1">
                            {userBadges.map((ub) => (
                                <div key={ub.id} className="flex items-center gap-3 p-2 hover:bg-muted/50 rounded-md transition-colors">
                                    <div className="text-2xl">{ub.badge?.icon || '🏆'}</div>
                                    <div className="flex-1 min-w-0">
                                        <p className="text-sm font-semibold truncate text-primary">{ub.badge?.name || 'Unknown Badge'}</p>
                                        <p className="text-[10px] text-muted-foreground truncate">
                                            Earned {ub.earnedAt?.toLocaleDateString()}
                                        </p>
                                    </div>
                                </div>
                            ))}
                        </div>
                    )}
                </ScrollArea>
                <DropdownMenuSeparator />
                <DropdownMenuItem asChild className="cursor-pointer justify-center text-primary font-medium">
                    <Link to={`/users/${currentUser.uid}`}>View All Badges</Link>
                </DropdownMenuItem>
            </DropdownMenuContent>
        </DropdownMenu>
    );
};
